<template>
  <section class="christmas-tree" :class="{ 'is-celebrating': celebrating }" aria-label="裝飾聖誕樹">
    <div ref="tree" class="christmas-tree-target">
      <span class="christmas-tree-trunk"></span>
      <span class="christmas-tree-branch branch-bottom"></span>
      <span class="christmas-tree-branch branch-middle"></span>
      <span class="christmas-tree-branch branch-top"></span>
      <button
        v-for="item in decorations"
        :key="item.id"
        class="christmas-decoration placed is-glowing-ornament"
        :class="'ornament-' + item.type"
        :style="{ left: item.x + 'px', top: item.y + 'px', '--ornament-glow': item.glow, visibility: dragged && dragged.id === item.id ? 'hidden' : 'visible' }"
        :aria-label="item.name + '，可拖曳調整位置'"
        @pointerdown.prevent.stop="startDrag($event, item.type, item.id)"
      >{{ item.symbol }}</button>
    </div>
    <div class="christmas-decoration-tray">
      <button
        v-for="(item, type) in ornamentTypes"
        :key="type"
        class="christmas-decoration"
        :class="'ornament-' + type"
        :style="{ '--ornament-glow': item.glow, visibility: usedTypes.includes(type) || (dragged && dragged.type === type) ? 'hidden' : 'visible' }"
        :aria-label="item.name + '，拖到樹上或按 Enter 放置'"
        :title="item.name"
        @pointerdown.prevent.stop="startDrag($event, type)"
        @keydown.enter.prevent="addDecoration(type, 90, 70 + decorations.length * 28)"
        @keydown.space.prevent="addDecoration(type, 90, 70 + decorations.length * 28)"
      >{{ item.symbol }}</button>
    </div>
    <span
      v-if="dragged"
      class="christmas-decoration christmas-decoration-dragging"
      :class="'ornament-' + dragged.type"
      :style="{ left: dragged.x + 'px', top: dragged.y + 'px', '--ornament-glow': ornamentTypes[dragged.type].glow }"
      aria-hidden="true"
    >{{ ornamentTypes[dragged.type].symbol }}</span>
    <div v-if="celebrating" class="christmas-celebration-backdrop" aria-hidden="true"></div>
    <div v-if="celebrating" class="christmas-snowfall" aria-hidden="true">
      <i
        v-for="flake in snowflakes"
        :key="flake.id"
        class="christmas-snowflake"
        :style="{ left: flake.left + '%', width: flake.size + 'px', height: flake.size + 'px', animationDelay: flake.delay + 's', '--flake-drift': flake.drift + 'px', '--flake-turn': flake.turn + 'deg' }"
      ></i>
    </div>
    <div
      v-if="snowmanVisible"
      ref="snowman"
      class="christmas-snowman"
      :class="{ 'is-dragging': snowmanDrag, 'is-smiling': snowmanHappy || snowmanReading, 'is-reading': snowmanReading, 'is-animating': snowmanAnimating, 'is-muscular': snowmanMuscular, 'is-cheering': snowmanCheering }"
      :style="snowmanPosition ? { left: snowmanPosition.x + 'px', top: snowmanPosition.y + 'px' } : null"
      role="button"
      tabindex="0"
      :aria-label="snowmanReading ? '小雪人開心讀書，可拖曳或用方向鍵移動' : snowmanHappy ? '小雪人開心地笑了！可拖曳或用方向鍵移動' : '小雪人，可拖曳或用方向鍵移到聖誕樹旁或課程上'"
      @pointerdown.prevent.stop="startSnowmanDrag"
      @keydown="moveSnowmanWithKeyboard"
    >
      <span v-if="!snowmanMuscular" class="christmas-snowman-body"></span>
      <svg v-else class="christmas-snowman-muscular-body" viewBox="0 0 104 148" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id="snowman-muscle-snow" cx="35%" cy="25%" r="85%">
            <stop offset=".35" stop-color="#ffffff" />
            <stop offset="1" stop-color="#d5e9ee" />
          </radialGradient>
        </defs>
        <!-- Rounded snowball muscles keep the original snowman's soft silhouette. -->
        <g fill="url(#snowman-muscle-snow)">
          <g v-if="!snowmanReading">
          <g class="christmas-snowman-flex-arm flex-left">
          <path d="M21 91 Q13 78 6 84 Q-1 89 4 99 Q11 112 27 104Z" />
          <path d="M4 94 Q0 81 5 71 Q10 65 16 72 L16 91Z" />
          <circle cx="12" cy="72" r="9" />
          </g>
          <g class="christmas-snowman-flex-arm flex-right">
          <path d="M83 91 Q91 78 98 84 Q105 89 100 99 Q93 112 77 104Z" />
          <path d="M100 94 Q104 81 99 71 Q94 65 88 72 L88 91Z" />
          <circle cx="92" cy="72" r="9" />
          </g>
          </g>
          <path d="M52 67 C24 67 10 84 10 109 C10 136 24 147 52 147 C80 147 94 136 94 109 C94 84 80 67 52 67Z" />
          <g class="christmas-snowman-flex-chest">
          <ellipse cx="35" cy="96" rx="17" ry="13" />
          <ellipse cx="69" cy="96" rx="17" ry="13" />
          </g>
          <ellipse cx="44" cy="117" rx="7" ry="6" />
          <ellipse cx="60" cy="117" rx="7" ry="6" />
          <ellipse cx="45" cy="130" rx="6" ry="5" />
          <ellipse cx="59" cy="130" rx="6" ry="5" />
        </g>
        <g fill="#374a4d">
          <circle cx="52" cy="101" r="2.5" />
          <circle cx="52" cy="115" r="2.5" />
          <circle cx="52" cy="130" r="2.5" />
        </g>
      </svg>
      <div class="christmas-snowman-face">
        <span class="christmas-snowman-head"><i class="christmas-snowman-eyelids" aria-hidden="true"></i></span>
        <span class="christmas-snowman-nose"></span>
        <span class="christmas-snowman-hat"></span>
        <span v-if="snowmanReading" class="christmas-snowman-glasses" aria-hidden="true"></span>
      </div>
      <span class="christmas-snowman-scarf"></span>
      <span v-if="snowmanCheering && !snowmanMuscular" class="christmas-snowman-cheer-arm cheer-left" aria-hidden="true"></span>
      <span v-if="snowmanCheering && !snowmanMuscular" class="christmas-snowman-cheer-arm cheer-right" aria-hidden="true"></span>
      <span v-if="snowmanCheering" class="christmas-snowman-cheer-text" role="status">Merry Christmas!</span>
      <span v-if="snowmanReading" class="christmas-snowman-book" aria-hidden="true"><b class="christmas-snowman-turning-page"></b><i></i><i>ABC</i></span>
      <span v-if="snowmanSpeech" class="christmas-snowman-speech" :style="snowmanPosition ? { left: Math.min(0, viewportWidth - snowmanPosition.x - 192) + 'px' } : null">{{ snowmanSpeech }}</span>
    </div>
    <span class="sr-only" role="status">{{ snowmanSpeech || (snowmanReading ? '小雪人開心讀書中！' : snowmanMuscular ? '雪停了，小雪人變成肌肉雪人！' : snowmanHappy ? '小雪人在聖誕樹旁開心地笑了！' : '') }}</span>
  </section>
</template>

<script>
export default {
  name: 'ChristmasTree',
  data () {
    return {
      ornamentTypes: {
        red: { name: '紅色彩球', symbol: '🔴', glow: '#FF3535' },
        gold: { name: '金色彩球', symbol: '🟡', glow: '#FFD53D' },
        blue: { name: '藍色彩球', symbol: '🔵', glow: '#328BFF' },
        star: { name: '星星', symbol: '⭐', glow: '#E8BC65' },
        bow: { name: '蝴蝶結', symbol: '🎀', glow: '#FF69A0' },
        bell: { name: '鈴鐺', symbol: '🔔', glow: '#FFD53D' }
      },
      decorations: [],
      usedTypes: [],
      dragged: null,
      nextId: 0,
      celebrating: false,
      snowmanVisible: true,
      viewportWidth: window.innerWidth,
      snowmanPosition: null,
      snowmanDrag: null,
      snowmanHappy: false,
      snowmanCheering: false,
      snowmanCheerTimer: null,
      snowmanReading: false,
      snowmanMuscular: false,
      snowmanPromptActive: true,
      snowmanPromptTimer: null,
      celebrationTimer: null,
      snowflakes: Array.from({ length: 80 }, (_, id) => ({
        id,
        left: (id * 37 % 100),
        size: 5 + id % 5 * 2,
        delay: (id % 20) * 0.15,
        drift: (id % 2 ? 1 : -1) * (40 + id % 5 * 25),
        turn: (id % 2 ? 1 : -1) * (360 + id % 4 * 180)
      }))
    }
  },
  computed: {
    snowmanAnimating () {
      return this.snowmanVisible && !this.snowmanDrag
    },
    snowmanSpeech () {
      return this.snowmanPromptVisible ? '請幫我布置聖誕樹 🎄' : ''
    },
    snowmanPromptVisible () {
      return this.snowmanVisible && !this.snowmanDrag && !this.snowmanCheering && this.snowmanPromptActive && !this.celebrating && this.usedTypes.length < Object.keys(this.ornamentTypes).length
    }
  },
  watch: {
    snowmanVisible (visible) {
      if (!visible) return
      this.$nextTick(() => {
        // Escape the tree's stacking context so the snowman stays above the page.
        if (this.$refs.snowman) document.body.appendChild(this.$refs.snowman)
      })
    }
  },
  mounted () {
    // The snowman is visible before decorating, so mount it above the page now.
    if (this.$refs.snowman) document.body.appendChild(this.$refs.snowman)
    this.cycleSnowmanPrompt()
    window.addEventListener('pointermove', this.moveDrag)
    window.addEventListener('pointerup', this.placeDecoration)
    window.addEventListener('pointercancel', this.cancelDrag)
    window.addEventListener('blur', this.cancelDrag)
  },
  beforeDestroy () {
    window.clearTimeout(this.snowmanCheerTimer)
    window.clearTimeout(this.snowmanPromptTimer)
    if (this.$refs.snowman) this.$refs.snowman.remove()
    window.clearTimeout(this.celebrationTimer)
    window.removeEventListener('pointermove', this.moveDrag)
    window.removeEventListener('pointerup', this.placeDecoration)
    window.removeEventListener('pointercancel', this.cancelDrag)
    window.removeEventListener('blur', this.cancelDrag)
  },
  methods: {
    cheerSnowman () {
      if (!this.snowmanHappy || this.snowmanReading || this.snowmanDrag) return
      window.clearTimeout(this.snowmanCheerTimer)
      this.snowmanCheering = true
      this.snowmanCheerTimer = window.setTimeout(() => {
        this.snowmanCheering = false
        this.snowmanCheerTimer = null
      }, 3000)
    },
    cycleSnowmanPrompt () {
      window.clearTimeout(this.snowmanPromptTimer)
      this.snowmanPromptTimer = null
      if (this.usedTypes.length === Object.keys(this.ornamentTypes).length) return
      this.snowmanPromptTimer = window.setTimeout(() => {
        this.snowmanPromptActive = !this.snowmanPromptActive
        this.cycleSnowmanPrompt()
      }, 4000)
    },
    startSnowmanDrag (event) {
      if (event.button !== 0 || this.dragged || this.snowmanDrag) return
      const bounds = event.currentTarget.getBoundingClientRect()
      window.clearTimeout(this.snowmanCheerTimer)
      this.snowmanCheerTimer = null
      this.snowmanCheering = false
      this.snowmanDrag = {
        pointerId: event.pointerId,
        offsetX: event.clientX - bounds.left,
        offsetY: event.clientY - bounds.top,
        originalPosition: this.snowmanPosition,
        originalHappy: this.snowmanHappy,
        originalReading: this.snowmanReading
      }
    },
    setSnowmanPosition (x, y) {
      this.viewportWidth = window.innerWidth
      const { width, height } = this.$refs.snowman.getBoundingClientRect()
      x = Math.max(0, Math.min(x, window.innerWidth - width))
      y = Math.max(0, Math.min(y, window.innerHeight - height))
      this.snowmanPosition = { x, y }
      const tree = this.$refs.tree.getBoundingClientRect()
      const gap = Math.max(tree.left - (x + width), x - tree.right, 0)
      this.snowmanHappy = gap <= 72 && Math.abs(y + height - tree.bottom) <= 140
      const centerX = x + width / 2
      const centerY = y + height / 2
      this.snowmanReading = Array.from(document.querySelectorAll('.home-page [data-snowman-course]')).some(course => {
        const bounds = course.getBoundingClientRect()
        return bounds.width > 0 && bounds.height > 0 && centerX >= bounds.left && centerX <= bounds.right &&
          centerY >= bounds.top && centerY <= bounds.bottom
      })
    },
    moveSnowmanWithKeyboard (event) {
      const steps = { ArrowLeft: [-20, 0], ArrowRight: [20, 0], ArrowUp: [0, -20], ArrowDown: [0, 20] }
      if (!steps[event.key] || this.snowmanDrag) return
      event.preventDefault()
      const bounds = this.$refs.snowman.getBoundingClientRect()
      window.clearTimeout(this.snowmanCheerTimer)
      this.snowmanCheerTimer = null
      this.snowmanCheering = false
      const [x, y] = steps[event.key]
      this.setSnowmanPosition(bounds.left + x, bounds.top + y)
    },
    startDrag (event, type, id = null) {
      if (event.button !== 0 || this.dragged || this.snowmanDrag || (id === null && this.usedTypes.includes(type))) return
      const bounds = event.currentTarget.getBoundingClientRect()
      this.dragged = {
        type,
        id,
        pointerId: event.pointerId,
        offsetX: event.clientX - bounds.left - bounds.width / 2,
        offsetY: event.clientY - bounds.top - bounds.height / 2,
        x: bounds.left + bounds.width / 2,
        y: bounds.top + bounds.height / 2
      }
    },
    moveDrag (event) {
      if (this.snowmanDrag && event.pointerId === this.snowmanDrag.pointerId) {
        this.setSnowmanPosition(event.clientX - this.snowmanDrag.offsetX, event.clientY - this.snowmanDrag.offsetY)
        return
      }
      if (!this.dragged || event.pointerId !== this.dragged.pointerId) return
      this.dragged.x = event.clientX - this.dragged.offsetX
      this.dragged.y = event.clientY - this.dragged.offsetY
    },
    cancelDrag () {
      if (this.snowmanDrag) {
        this.snowmanPosition = this.snowmanDrag.originalPosition
        this.snowmanHappy = this.snowmanDrag.originalHappy
        this.snowmanReading = this.snowmanDrag.originalReading
        this.snowmanDrag = null
      }
      this.dragged = null
    },
    addDecoration (type, x, y) {
      if (this.usedTypes.includes(type)) return
      this.decorations.push({ ...this.ornamentTypes[type], type, id: this.nextId++, x, y })
      this.usedTypes.push(type)
      if (this.usedTypes.length === Object.keys(this.ornamentTypes).length) {
        window.clearTimeout(this.snowmanPromptTimer)
        this.snowmanPromptTimer = null
        this.snowmanPromptActive = false
        this.celebrating = true
        this.celebrationTimer = window.setTimeout(() => {
          this.celebrating = false
          this.snowmanMuscular = true
          this.snowmanVisible = true
          this.celebrationTimer = null
        }, 8000)
      }
    },
    placeDecoration (event) {
      if (this.snowmanDrag && event.pointerId === this.snowmanDrag.pointerId) {
        this.moveDrag(event)
        this.snowmanDrag = null
        this.cheerSnowman()
        return
      }
      if (!this.dragged || event.pointerId !== this.dragged.pointerId) return
      this.moveDrag(event)
      const bounds = this.$refs.tree.getBoundingClientRect()
      const scale = bounds.width / 180
      let x = (this.dragged.x - bounds.left) / scale
      let y = (this.dragged.y - bounds.top) / scale
      const edgePadding = 24
      const nearTreeTop = this.dragged.type === 'star' && Math.abs(x - 90) <= 48 && y >= -48 && y <= 48
      const onTree = [
        { top: 0, height: 135, width: 110 },
        { top: 45, height: 145, width: 145 },
        { top: 92, height: 160, width: 180 }
      ].some(layer => y >= layer.top - edgePadding && y <= layer.top + layer.height + edgePadding &&
        Math.abs(x - 90) <= Math.min(layer.height, Math.max(0, y - layer.top)) / layer.height * layer.width / 2 + edgePadding)
      if (nearTreeTop) {
        x = 90
        y = 0
      }
      if (onTree || nearTreeTop) {
        const existing = this.decorations.find(item => item.id === this.dragged.id)
        if (existing) {
          existing.x = x
          existing.y = y
        } else {
          this.addDecoration(this.dragged.type, x, y)
        }
      }
      this.cancelDrag()
    }
  }
}
</script>
